// utils/bugLog.ts
// Lightweight, dependency-free capture buffer backing the "Report a problem"
// button (components/BugReporter.tsx). Ported from swa-rprs/client/src/utils/bugLog.js
// (TypeScript, minus rprs's app-specific cold-start-retry hook).
//
// Two ring buffers in module scope: recent console.* lines + window error events.
// installBugLog() patches console.* once (idempotent) and registers the window
// listeners; call it at app boot (index.tsx) BEFORE React renders. Originals are
// always called through, so dev-tools behavior is unchanged.

export type LogEntry = { level: string; ts: string; msg: string };
export type EventEntry = {
  type: string; ts: string; message: string;
  source?: string | null; line?: number | null; col?: number | null; stack?: string | null;
};
export type ClientContext = Record<string, unknown>;

const LOG_LIMIT = 120;
const EVENT_LIMIT = 40;
const MAX_ARG_LEN = 2000;

const logBuffer: LogEntry[] = [];
const eventBuffer: EventEntry[] = [];
let installed = false;

function push<T>(buf: T[], entry: T, limit: number) {
  buf.push(entry);
  if (buf.length > limit) buf.splice(0, buf.length - limit);
}

// Best-effort, never-throw serialization of a single console argument.
function serializeArg(arg: unknown): string {
  if (arg == null) return String(arg);
  if (typeof arg === 'string') return arg.length > MAX_ARG_LEN ? arg.slice(0, MAX_ARG_LEN) + '…' : arg;
  if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg);
  if (arg instanceof Error) return `${arg.name}: ${arg.message}${arg.stack ? '\n' + arg.stack : ''}`.slice(0, MAX_ARG_LEN);
  try {
    const seen = new WeakSet<object>();
    const s = JSON.stringify(arg, (_k, v) => {
      if (typeof v === 'object' && v !== null) {
        if (seen.has(v)) return '[Circular]';
        seen.add(v);
      }
      return v;
    });
    return s == null ? String(arg) : (s.length > MAX_ARG_LEN ? s.slice(0, MAX_ARG_LEN) + '…' : s);
  } catch {
    try { return String(arg).slice(0, MAX_ARG_LEN); } catch { return '[unserializable]'; }
  }
}

export function installBugLog(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const c = console as unknown as Record<string, (...a: unknown[]) => void>;
  const levels = ['log', 'info', 'warn', 'error', 'debug'];
  for (const level of levels) {
    const original = c[level] ? c[level].bind(console) : null;
    c[level] = (...args: unknown[]) => {
      try {
        push(logBuffer, { level, ts: new Date().toISOString(), msg: args.map(serializeArg).join(' ') }, LOG_LIMIT);
      } catch { /* never let logging break the app */ }
      if (original) original(...args);
    };
  }

  window.addEventListener('error', (e: ErrorEvent) => {
    push(eventBuffer, {
      type: 'window.error', ts: new Date().toISOString(),
      message: e?.message || String(e), source: e?.filename || null,
      line: e?.lineno ?? null, col: e?.colno ?? null,
      stack: e?.error?.stack ? String(e.error.stack).slice(0, MAX_ARG_LEN) : null,
    }, EVENT_LIMIT);
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e?.reason;
    push(eventBuffer, {
      type: 'unhandledrejection', ts: new Date().toISOString(),
      message: reason instanceof Error ? `${reason.name}: ${reason.message}` : serializeArg(reason),
      stack: reason instanceof Error && reason.stack ? String(reason.stack).slice(0, MAX_ARG_LEN) : null,
    }, EVENT_LIMIT);
  });
}

// Snapshots — copies so callers can't mutate the live buffers.
export function snapshotLogs(): LogEntry[] { return logBuffer.slice(); }
export function snapshotEvents(): EventEntry[] { return eventBuffer.slice(); }

// Stable client-side context anyone can attach to a report.
export function snapshotClientContext(): ClientContext {
  if (typeof window === 'undefined') return {};
  return {
    url: window.location.href,
    route: window.location.pathname + window.location.search + window.location.hash,
    referrer: document.referrer || null,
    viewport: { width: window.innerWidth, height: window.innerHeight, devicePixelRatio: window.devicePixelRatio || 1 },
    screen: { width: window.screen?.width ?? null, height: window.screen?.height ?? null },
    userAgent: navigator.userAgent,
    language: navigator.language,
    online: navigator.onLine,
    capturedAt: new Date().toISOString(),
  };
}
